import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Sparkles, Plus, MapPin, Clock, CheckCircle2, AlertTriangle,
  Trash2, RefreshCw, ListChecks,
} from 'lucide-react';
import { toast } from 'sonner';
import BuildingFilter from '../components/BuildingFilter';

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR'];
const COMPLETE = [...MANAGE, 'TECHNICIAN', 'STOREKEEPER', 'SECURITY_OFFICER'];

const FREQ_LABEL = {
  twice_daily: 'Twice daily',
  daily: 'Daily',
  alt_daily: 'Alt. days',
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

const AREA_TYPES = [
  'lobby', 'corridor', 'restroom', 'parking', 'garden',
  'staircase', 'lift', 'rooftop', 'pool', 'gym', 'office', 'other',
];

const TYPE_COLOR = {
  lobby: 'sky', corridor: 'indigo', restroom: 'rose', parking: 'slate',
  garden: 'emerald', staircase: 'amber', lift: 'violet', rooftop: 'cyan',
  pool: 'teal', gym: 'fuchsia', office: 'blue', other: 'gray',
};

function todayIso() { return new Date().toISOString().slice(0, 10); }

export default function Housekeeping() {
  const { user } = useContext(AuthContext);
  const canManage = MANAGE.includes(user?.role);
  const canComplete = COMPLETE.includes(user?.role);

  const [areas, setAreas] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [summary, setSummary] = useState({ total_areas: 0, due_today: 0, overdue: 0, completed_today: 0, pending_tasks: 0 });
  const [buildingFilter, setBuildingFilter] = useState('all');
  const [showNewArea, setShowNewArea] = useState(false);
  const [showCompleteTask, setShowCompleteTask] = useState(null);
  const [tab, setTab] = useState('tasks');
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const bQ = buildingFilter !== 'all' ? `?building_id=${buildingFilter}` : '';
      const [a, t, b, s, sm] = await Promise.all([
        axios.get(`${API}/housekeeping/areas${bQ}`, { withCredentials: true }),
        axios.get(`${API}/housekeeping/tasks${bQ}`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true }),
        axios.get(`${API}/staff`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/housekeeping/summary${bQ}`, { withCredentials: true }),
      ]);
      setAreas(a.data || []);
      setTasks(t.data || []);
      setBuildings(b.data || []);
      setStaff(s.data || []);
      setSummary(sm.data || {});
    } catch (e) {
      toast.error('Failed to load housekeeping data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [buildingFilter]);

  const buildingName = (id) => buildings.find(b => b.id === id)?.name || '—';
  const staffName = (id) => staff.find(s => s.id === id)?.name || (id ? '—' : 'Unassigned');

  const autoGenerate = async () => {
    try {
      const r = await axios.post(`${API}/housekeeping/auto-generate`, {}, { withCredentials: true });
      toast.success(`Generated ${r.data.created} task(s) for today`);
      fetchAll();
    } catch { toast.error('Auto-generation failed'); }
  };

  return (
    <Layout>
      <div data-testid="housekeeping-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Sparkles className="w-7 h-7 text-amber-500" /> Housekeeping
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Area-by-area cleaning checklists, recurring frequency, and daily task tracking.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <BuildingFilter value={buildingFilter} onChange={setBuildingFilter} buildings={buildings} />
            {canManage && (
              <>
                <Button data-testid="auto-gen-btn" variant="outline" onClick={autoGenerate}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Generate Today
                </Button>
                <NewAreaButton open={showNewArea} setOpen={setShowNewArea} buildings={buildings} staff={staff} onSaved={fetchAll} />
              </>
            )}
          </div>
        </div>

        {/* Summary KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <KpiCard label="Total Areas" value={summary.total_areas} icon={MapPin} color="sky" testId="kpi-total" />
          <KpiCard label="Due Today" value={summary.due_today} icon={Clock} color="amber" testId="kpi-due" />
          <KpiCard label="Overdue" value={summary.overdue} icon={AlertTriangle} color="rose" testId="kpi-overdue" />
          <KpiCard label="Completed Today" value={summary.completed_today} icon={CheckCircle2} color="emerald" testId="kpi-completed" />
          <KpiCard label="Pending Tasks" value={summary.pending_tasks} icon={ListChecks} color="violet" testId="kpi-pending" />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="tasks" data-testid="tab-tasks">Today's Tasks ({tasks.filter(t => t.status !== 'completed').length})</TabsTrigger>
            <TabsTrigger value="areas" data-testid="tab-areas">Areas ({areas.length})</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="tasks" className="mt-4 space-y-3">
            {loading ? <div className="text-muted-foreground text-sm">Loading…</div> :
              tasks.filter(t => t.status !== 'completed').length === 0 ? (
                <Card className="p-8 text-center text-muted-foreground">
                  No pending tasks. Click <strong>Generate Today</strong> to schedule today's housekeeping.
                </Card>
              ) : (
                tasks.filter(t => t.status !== 'completed').map(tk => (
                  <TaskRow key={tk.id} task={tk} buildingName={buildingName(tk.building_id)}
                    onComplete={() => setShowCompleteTask(tk)} canComplete={canComplete} />
                ))
              )}
          </TabsContent>

          <TabsContent value="areas" className="mt-4 space-y-3">
            {loading ? <div className="text-muted-foreground text-sm">Loading…</div> :
              areas.length === 0 ? (
                <Card className="p-8 text-center text-muted-foreground">
                  No areas defined yet. {canManage && 'Click "Add Area" to start.'}
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {areas.map(a => (
                    <AreaCard key={a.id} area={a} buildingName={buildingName(a.building_id)}
                      staffName={staffName(a.assigned_staff_id)} canManage={canManage} onRefresh={fetchAll} />
                  ))}
                </div>
              )}
          </TabsContent>

          <TabsContent value="history" className="mt-4 space-y-2">
            {tasks.filter(t => t.status === 'completed').slice(0, 100).map(tk => (
              <Card key={tk.id} className="p-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-foreground">{tk.area_name}</div>
                  <div className="text-xs text-muted-foreground">{buildingName(tk.building_id)} · {tk.scheduled_date}</div>
                </div>
                <span className="px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 text-xs font-semibold">
                  Completed {tk.completed_at?.slice(0, 10)}
                </span>
              </Card>
            ))}
            {tasks.filter(t => t.status === 'completed').length === 0 && (
              <Card className="p-8 text-center text-muted-foreground">No completed tasks yet.</Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {showCompleteTask && (
        <CompleteTaskDialog
          task={showCompleteTask}
          onClose={() => setShowCompleteTask(null)}
          onSaved={() => { setShowCompleteTask(null); fetchAll(); }}
        />
      )}
    </Layout>
  );
}

function KpiCard({ label, value, icon: Icon, color, testId }) {
  const colorClass = {
    sky: 'text-sky-600 dark:text-sky-400',
    amber: 'text-amber-600 dark:text-amber-400',
    rose: 'text-rose-600 dark:text-rose-400',
    emerald: 'text-emerald-600 dark:text-emerald-400',
    violet: 'text-violet-600 dark:text-violet-400',
  }[color];
  return (
    <Card data-testid={testId} className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className={`text-2xl font-bold mt-1 ${colorClass}`}>{value}</div>
        </div>
        <Icon className={`w-6 h-6 opacity-30 ${colorClass}`} />
      </div>
    </Card>
  );
}

function TaskRow({ task, buildingName, onComplete, canComplete }) {
  const doneCount = (task.checklist || []).filter(c => c.done).length;
  const totalCount = (task.checklist || []).length;
  return (
    <Card data-testid={`task-row-${task.id}`} className="p-4 flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-foreground">{task.area_name}</div>
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            {task.scheduled_date}
          </span>
        </div>
        <div className="text-xs text-muted-foreground mt-1">{buildingName}</div>
        {totalCount > 0 && (
          <div className="text-xs text-muted-foreground mt-1">
            Checklist: {doneCount} / {totalCount} done
          </div>
        )}
      </div>
      {canComplete && (
        <Button data-testid={`complete-task-${task.id}`} size="sm" onClick={onComplete}>
          <CheckCircle2 className="w-4 h-4 mr-1" /> Complete
        </Button>
      )}
    </Card>
  );
}

function AreaCard({ area, buildingName, staffName, canManage, onRefresh }) {
  const color = TYPE_COLOR[area.area_type] || 'gray';
  const today = todayIso();
  const overdue = area.next_due_date && area.next_due_date < today;
  const dueToday = area.next_due_date === today;

  const handleDelete = async () => {
    if (!window.confirm(`Delete area "${area.name}"? This will also remove its pending tasks.`)) return;
    try {
      await axios.delete(`${API}/housekeeping/areas/${area.id}`, { withCredentials: true });
      toast.success('Area deleted');
      onRefresh();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <Card data-testid={`area-card-${area.id}`} className="p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-foreground truncate">{area.name}</div>
          <div className="text-xs text-muted-foreground">{buildingName}</div>
          {area.location_detail && (
            <div className="text-xs text-muted-foreground italic">{area.location_detail}</div>
          )}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full bg-${color}-100 text-${color}-700 dark:bg-${color}-900/30 dark:text-${color}-300 uppercase tracking-wide`}>
          {area.area_type}
        </span>
      </div>
      <div className="mt-3 space-y-1 text-xs">
        <div className="text-muted-foreground">Frequency: <span className="font-semibold text-foreground">{FREQ_LABEL[area.frequency] || area.frequency}</span></div>
        <div className="text-muted-foreground">Assigned: <span className="text-foreground">{staffName}</span></div>
        {area.next_due_date && (
          <div className={`text-xs ${overdue ? 'text-rose-600 dark:text-rose-400 font-semibold' : dueToday ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-muted-foreground'}`}>
            Next due: {area.next_due_date}{overdue ? ' (overdue)' : dueToday ? ' (today)' : ''}
          </div>
        )}
        {area.last_cleaned_at && (
          <div className="text-xs text-muted-foreground">Last cleaned: {area.last_cleaned_at.slice(0, 10)}</div>
        )}
        {(area.default_checklist || []).length > 0 && (
          <div className="text-xs text-muted-foreground">{area.default_checklist.length}-step checklist</div>
        )}
      </div>
      {canManage && (
        <div className="mt-3 flex justify-end">
          <Button data-testid={`delete-area-${area.id}`} variant="ghost" size="sm" onClick={handleDelete} className="text-rose-600">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </Card>
  );
}

function NewAreaButton({ open, setOpen, buildings, staff, onSaved }) {
  const [form, setForm] = useState({
    building_id: '', name: '', area_type: 'lobby', location_detail: '',
    assigned_staff_id: '', frequency: 'daily', notes: '',
    default_checklist: 'Mop floor\nEmpty bins\nWipe surfaces',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.building_id || !form.name) {
      toast.error('Building and name are required');
      return;
    }
    setSaving(true);
    try {
      const checklist = form.default_checklist.split('\n').map(s => s.trim()).filter(Boolean);
      await axios.post(`${API}/housekeeping/areas`, {
        ...form, default_checklist: checklist,
        assigned_staff_id: form.assigned_staff_id || null,
      }, { withCredentials: true });
      toast.success('Area created');
      setOpen(false);
      setForm({ building_id: '', name: '', area_type: 'lobby', location_detail: '', assigned_staff_id: '', frequency: 'daily', notes: '', default_checklist: 'Mop floor\nEmpty bins\nWipe surfaces' });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create area');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-area-btn"><Plus className="w-4 h-4 mr-1" /> Add Area</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New Housekeeping Area</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Building *</Label>
            <Select value={form.building_id} onValueChange={v => setForm({ ...form, building_id: v })}>
              <SelectTrigger data-testid="building-select"><SelectValue placeholder="Pick a building" /></SelectTrigger>
              <SelectContent>
                {buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Name *</Label>
            <Input data-testid="area-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Lobby Floor 1" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Area Type</Label>
              <Select value={form.area_type} onValueChange={v => setForm({ ...form, area_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AREA_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Frequency</Label>
              <Select value={form.frequency} onValueChange={v => setForm({ ...form, frequency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FREQ_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Location Detail</Label>
            <Input value={form.location_detail} onChange={e => setForm({ ...form, location_detail: e.target.value })} placeholder="e.g. East Wing, Floor 3" />
          </div>
          <div>
            <Label>Assign to Staff (optional)</Label>
            <Select value={form.assigned_staff_id || 'none'} onValueChange={v => setForm({ ...form, assigned_staff_id: v === 'none' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Default Checklist (one item per line)</Label>
            <Textarea data-testid="checklist-input" rows={4} value={form.default_checklist} onChange={e => setForm({ ...form, default_checklist: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="save-area-btn" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Create Area'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompleteTaskDialog({ task, onClose, onSaved }) {
  const [checklist, setChecklist] = useState(task.checklist || []);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const toggle = (i) => {
    const next = [...checklist];
    next[i] = { ...next[i], done: !next[i].done };
    setChecklist(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/housekeeping/tasks/${task.id}/complete`,
        { checklist, completion_notes: notes }, { withCredentials: true });
      toast.success('Task marked complete · area roll-forward scheduled');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to complete task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Complete: {task.area_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-xs text-muted-foreground">Scheduled: {task.scheduled_date}</div>
          {checklist.length > 0 ? (
            <div className="space-y-2">
              <Label>Checklist</Label>
              {checklist.map((c, i) => (
                <label key={i} className="flex items-center gap-2 p-2 rounded border border-border hover:bg-muted cursor-pointer">
                  <input data-testid={`check-${i}`} type="checkbox" checked={!!c.done} onChange={() => toggle(i)} />
                  <span className={`text-sm ${c.done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>{c.name}</span>
                </label>
              ))}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground italic">No checklist defined for this area.</div>
          )}
          <div>
            <Label>Completion Notes (optional)</Label>
            <Textarea data-testid="completion-notes-input" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any observations or issues found" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="confirm-complete-btn" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Mark Complete'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
